import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useFilters,
} from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "jalali-moment";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useSelector } from "react-redux";
import DateFilterBox from "./DateFilterBox";
import "../../../../assets/fonts/IRANSans-normal";
import TransactionInfoModal from "./TransactionInfoModal";
import {
  Search, FileSpreadsheet, FileText, X, Filter, CheckCircle2,
  XCircle, Clock, Ban, ListFilter, Eye, Layers, ChevronRight,
  ChevronLeft, ListOrdered, Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Main
 * ================================================================ */
const TransactionsTable = ({ transactionList: data, isEnglish: isEnglishProp }) => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = isEnglishProp ?? i18n.language === "en";

  /* ---------- locale helper ---------- */
  const formatNumber = useCallback(
    (num) => {
      const n = Number(num || 0);
      return isEnglish
        ? n.toLocaleString("en-US")
        : n.toLocaleString("fa-IR");
    },
    [isEnglish]
  );

  /* ---------- statusConfig با ترجمه ---------- */
  const statusConfig = useMemo(() => ({
    success: {
      label: t("transactionsTable.selectStatus.success"),
      icon: CheckCircle2,
      chip: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
      dot: "bg-emerald-400 shadow-[0_0_6px_rgba(16,185,129,0.9)]",
    },
    fail: {
      label: t("transactionsTable.selectStatus.fail"),
      icon: XCircle,
      chip: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
      dot: "bg-rose-400 shadow-[0_0_6px_rgba(244,63,94,0.9)]",
    },
    pending: {
      label: t("transactionsTable.selectStatus.pending"),
      icon: Clock,
      chip: "bg-amber-500/15 text-amber-800 dark:text-amber-200 border-amber-500/30",
      dot: "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.9)]",
    },
    cancel: {
      label: t("transactionsTable.selectStatus.cancel"),
      icon: Ban,
      chip: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
      dot: "bg-slate-400",
    },
  }), [t]);

  const getStatusMeta = (status) => statusConfig[status] || statusConfig.cancel;

  const [selectedMinDate, setSelectedMinDate] = useState(null);
  const [selectedMaxDate, setSelectedMaxDate] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(t("transactionsTable.filterByStatus"));
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [transactionModalData, setTransactionModalData] = useState(null);
  const [lastCount, setLastCount] = useState("");

  /* ---------- Sort ---------- */
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [data]);

  /* ---------- Modal ---------- */
  const openModal = (row) => {
    setTransactionModalData(row);
    setIsTransactionModalOpen(true);
  };
  const closeModal = () => {
    setIsTransactionModalOpen(false);
    setTransactionModalData(null);
  };

  /* ---------- Columns ---------- */
  const columns = useMemo(
    () => [
      {
        Header: t("transactionsTable.columns.row"),
        Cell: ({ row }) => (
          <span className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
            {formatNumber(row.index + 1)}
          </span>
        ),
        disableSortBy: true,
      },
      {
        Header: t("transactionsTable.columns.botName"),
        accessor: (row) => row?.business?.username,
        Cell: ({ value }) => (
          <span className="text-xs font-bold text-slate-800 dark:text-amber-100">
            {value || "---"}
          </span>
        ),
      },
      {
        Header: t("transactionsTable.columns.reason"),
        accessor: "subId",
        Cell: ({ value }) => {
          const isPurchase = value && value !== "0" && value !== null && value !== undefined;
          return (
            <span
              className={`
                inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border
                ${isPurchase
                  ? "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30"
                  : "bg-amber-500/15 text-amber-800 dark:text-amber-200 border-amber-500/30"
                }
              `}
            >
              {isPurchase
                ? t("transactionsTable.reasonTypes.planPurchase")
                : t("transactionsTable.reasonTypes.addCredit")}
            </span>
          );
        },
      },
      {
        Header: t("transactionsTable.columns.paymentDate"),
        accessor: "createdAt",
        Cell: ({ value }) => (
          <span className={`text-[11px] tabular-nums text-slate-600 dark:!text-slate-300 whitespace-nowrap ${isEnglish && 'font-sans'}`}>
            {moment.utc(value).local().locale(isEnglish ? "en" : "fa").format("jYYYY/jMM/jDD HH:mm")}
          </span>
        ),
      },
      {
        Header: t("transactionsTable.columns.amount"),
        accessor: (row) => row?.paidAmount,
        Cell: ({ value }) => (
          <span className={`text-xs font-extrabold tabular-nums text-amber-600 dark:text-amber-300 whitespace-nowrap ${isEnglish && 'font-sans'}`}>
            {value != null ? formatNumber(value) : (isEnglish ? "0" : "۰")}
          </span>
        ),
      },
      {
        Header: t("transactionsTable.columns.gateway"),
        accessor: "ipg",
        Cell: ({ value }) => (
          <span className="text-[11px] font-bold text-slate-600 dark:!text-slate-300 whitespace-nowrap">
            {value === "zibal"
              ? t("transactionsTable.gateways.zibal")
              : value === "zarinpal"
                ? t("transactionsTable.gateways.zarinpal")
                : value === "wallet"
                  ? t("transactionsTable.gateways.wallet")
                  : t("transactionsTable.gateways.unknown")}
          </span>
        ),
      },
      {
        Header: t("transactionsTable.columns.info"),
        Cell: ({ row }) => (
          <button
            onClick={() => openModal(row.original)}
            title={t("transactionsTable.viewPaymentInfo")}
            className="
              h-8 w-8 inline-flex items-center justify-center
              rounded-lg
              text-teal-700 dark:text-teal-300
              bg-teal-500/10 hover:bg-teal-500/20
              border border-teal-500/30 hover:border-teal-400/60
              transition-all duration-200
              hover:scale-105 active:scale-95
            "
          >
            <Eye size={13} />
          </button>
        ),
      },
      {
        Header: t("transactionsTable.columns.status"),
        accessor: "status",
        Cell: ({ value }) => {
          const meta = getStatusMeta(value);
          const Icon = meta.icon;
          return (
            <span
              className={`
                inline-flex items-center gap-1 px-2 py-1 rounded-lg
                text-[10px] font-bold border
                ${meta.chip}
              `}
            >
              <Icon size={10} />
              {meta.label}
            </span>
          );
        },
      },
    ],
    [t, isEnglish, formatNumber]
  );

  /* ---------- Date filter sync ---------- */
  useEffect(() => {
    if (selectedMinDate) {
      const m = moment
        .from(
          `${selectedMinDate.year}/${selectedMinDate.month}/${selectedMinDate.day}`,
          "fa",
          "YYYY/MM/DD"
        )
        .startOf("day");
      setMinDate(m.toISOString());
    } else {
      setMinDate("");
    }

    if (selectedMaxDate) {
      const m = moment
        .from(
          `${selectedMaxDate.year}/${selectedMaxDate.month}/${selectedMaxDate.day}`,
          "fa",
          "YYYY/MM/DD"
        )
        .endOf("day");
      setMaxDate(m.toISOString());
    } else {
      setMaxDate("");
    }
  }, [selectedMinDate, selectedMaxDate]);

  /* ---------- Filtered data ---------- */
  const filteredData = useMemo(() => {
    if (!Array.isArray(sortedData)) return [];

    const result = sortedData.filter((item) => {
      const createdDate = item.createdAt ? moment(item.createdAt) : null;

      const matchesDate =
        (!minDate || (createdDate && createdDate.isSameOrAfter(moment(minDate)))) &&
        (!maxDate || (createdDate && createdDate.isSameOrBefore(moment(maxDate))));

      const matchesName =
        item?.planId?.toLowerCase()?.includes(globalFilter?.toLowerCase()) ||
        item?.business?.username?.toLowerCase()?.includes(globalFilter?.toLowerCase()) ||
        item?.id?.toString()?.includes(globalFilter) ||
        item?.refId?.toString()?.includes(globalFilter);

      const matchesStatus =
        statusFilter === "" ||
        (statusFilter === "success" && item.status === "success") ||
        (statusFilter === "fail" && item.status === "fail") ||
        (statusFilter === "pending" && item.status === "pending") ||
        (statusFilter === "cancel" && item.status === "cancel");

      return matchesName && matchesDate && matchesStatus;
    });

    return lastCount ? result.slice(0, Number(lastCount)) : result;
  }, [sortedData, globalFilter, minDate, maxDate, statusFilter, lastCount]);

  /* ---------- Table ---------- */
  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 5 },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = tableInstance;

  /* ---------- Exports ---------- */
  const exportToExcel = () => {
    const mappedData = filteredData.map((item) => ({
      [t("transactionsTable.excel.columns.transactionCode")]: item.id,
      [t("transactionsTable.excel.columns.amount")]: Number(item.paidAmount).toLocaleString("en-US"),
      [t("transactionsTable.excel.columns.date")]: moment
        .utc(item.createdAt)
        .local()
        .locale("fa")
        .format("jYYYY/jMM/jDD HH:mm"),
      [t("transactionsTable.excel.columns.status")]: getStatusMeta(item.status).label,
    }));

    const ws = XLSX.utils.json_to_sheet(mappedData, { origin: "A2" });
    XLSX.utils.sheet_add_aoa(ws, [[t("transactionsTable.excel.title")]], { origin: "A1" });
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
    ws["!cols"] = [{ wch: 40 }, { wch: 20 }, { wch: 25 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("transactionsTable.excel.sheetName"));
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(dataBlob, t("transactionsTable.excel.fileName"));
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFont("Vazir");
    const tableColumn = [
      t("transactionsTable.pdf.columns.trackingCode"),
      t("transactionsTable.pdf.columns.amount"),
      t("transactionsTable.pdf.columns.date"),
      t("transactionsTable.pdf.columns.status"),
    ];
    const tableRows = filteredData.map((item) => [
      item.id,
      item.paidAmount,
      moment.utc(item.createdAt).local().locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      getStatusMeta(item.status).label,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { font: "Vazir", fontStyle: "normal", halign: "center" },
      headStyles: { halign: "center" },
    });
    doc.save(t("transactionsTable.pdf.fileName"));
  };

  /* ---------- Reset ---------- */
  const resetFilters = () => {
    setGlobalFilter("");
    setStatusFilter("");
    setSelectedStatus(t("transactionsTable.filterByStatus"));
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
    setLastCount("");
    tableInstance.state.pageIndex = 0;
  };

  const hasActiveFilters =
    globalFilter ||
    statusFilter ||
    selectedMinDate ||
    selectedMaxDate ||
    lastCount;

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div className="max-w-5xl mx-auto">

      {/* ============================================================
       *  Filters Row
       * ============================================================ */}
      <div className="space-y-3 mb-4">

        {/* Row 1: Search + Reset */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0 group">
            <Search
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
            />
            <input
              id="search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder={t("transactionsTable.searchPlaceholder")}
              className="
                w-full pr-9 pl-3 py-2.5 rounded-xl text-sm
                bg-white/60 dark:bg-slate-800/60
                text-slate-800 dark:text-slate-100
                border border-teal-500/20 dark:border-teal-400/20
                focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                transition-all duration-200
                placeholder:text-slate-400 dark:placeholder:text-slate-500
              "
            />
            {globalFilter && (
              <button
                type="button"
                onClick={() => setGlobalFilter("")}
                aria-label={t("transactionsTable.clearSearch")}
                className="absolute left-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <button
            onClick={resetFilters}
            disabled={!hasActiveFilters}
            title={t("transactionsTable.resetTitle")}
            className={`
              shrink-0 flex items-center justify-center gap-1.5
              h-10 px-3 sm:px-4 rounded-xl
              text-xs font-bold
              transition-all duration-300
              whitespace-nowrap
              ${hasActiveFilters
                ? "text-amber-900 dark:text-amber-100 bg-gradient-to-l from-amber-300/70 to-amber-400/40 border border-amber-300/50 hover:from-amber-300 hover:to-amber-400 hover:scale-[1.03] active:scale-95 shadow-[0_4px_16px_-6px_rgba(251,191,36,0.6)]"
                : "text-slate-400 dark:text-slate-600 bg-slate-100/50 dark:bg-slate-800/40 border border-slate-200/50 dark:border-slate-700/50 cursor-not-allowed"
              }
            `}
          >
            <X size={13} />
            <span className="hidden sm:inline">{t("transactionsTable.reset")}</span>
          </button>
        </div>

        {/* Row 2: Filters + Exports */}
        <div className="flex flex-wrap items-center gap-2">

          {/* Status dropdown */}
          <Menu as="div" className="relative inline-block text-right">
            <Menu.Button
              className="
                inline-flex items-center gap-2
                rounded-xl px-3 py-2.5
                text-xs sm:text-sm font-semibold
                text-slate-700 dark:text-slate-200
                bg-white/60 dark:bg-slate-800/60
                border border-teal-500/20 dark:border-teal-400/20
                backdrop-blur-md
                transition-all duration-200
                hover:border-amber-300/60
                hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.4)]
                whitespace-nowrap
              "
            >
              <Filter size={13} className="text-teal-600/70 dark:text-teal-300/70 shrink-0" />
              <span className="truncate max-w-[140px]">{selectedStatus}</span>
              <ChevronDownIcon className="-ml-1 size-4 text-slate-400 shrink-0" />
            </Menu.Button>

            <Menu.Items
              className="
                absolute right-0 z-30 mt-2 min-w-[180px]
                origin-top-right rounded-xl overflow-hidden
                bg-white/95 dark:bg-slate-900/95
                backdrop-blur-xl
                shadow-[0_20px_60px_-20px_rgba(15,118,110,0.5)]
                dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]
                border border-teal-500/20 dark:border-teal-400/20
                focus:outline-none
              "
            >
              <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
              <div className="py-1">
                {[
                  { value: "success", icon: CheckCircle2, tone: "text-emerald-500" },
                  { value: "fail", icon: XCircle, tone: "text-rose-500" },
                  { value: "pending", icon: Clock, tone: "text-amber-500" },
                  { value: "cancel", icon: Ban, tone: "text-slate-500" },
                ].map(({ value, icon: Icon, tone }) => (
                  <Menu.Item key={value}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStatus(t(`transactionsTable.selectStatus.${value}`));
                          setStatusFilter(value);
                        }}
                        className={`
                          flex w-full items-center justify-end gap-2
                          px-4 py-2.5 text-xs sm:text-sm font-medium
                          text-slate-700 dark:text-slate-200
                          transition-colors duration-200
                          ${active ? "bg-amber-300/20 dark:bg-amber-400/10" : ""}
                          ${statusFilter === value ? "bg-teal-500/10 dark:bg-teal-400/10" : ""}
                        `}
                      >
                        <span>{t(`transactionsTable.selectStatus.${value}`)}</span>
                        <Icon size={14} className={tone} />
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Menu>

          {/* Count select */}
          <div className="relative shrink-0">
            <ListOrdered
              size={13}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-600/70 dark:text-teal-300/70 pointer-events-none"
            />
            <select
              value={lastCount}
              onChange={(e) => setLastCount(e.target.value ? Number(e.target.value) : "")}
              className="
                appearance-none pr-9 pl-8 py-2.5 rounded-xl text-xs sm:text-sm font-semibold
                bg-white/60 dark:bg-slate-800/60
                text-slate-700 dark:text-slate-200
                border border-teal-500/20 dark:border-teal-400/20
                focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                transition-all duration-200
                cursor-pointer
              "
            >
              <option value="">{t("transactionsTable.countOptions.all")}</option>
              <option value="5">{t("transactionsTable.countOptions.last5")}</option>
              <option value="20">{t("transactionsTable.countOptions.last20")}</option>
              <option value="50">{t("transactionsTable.countOptions.last50")}</option>
              <option value="100">{t("transactionsTable.countOptions.last100")}</option>
            </select>
            <ChevronDownIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Date filter */}
          <div className="shrink-0">
            <DateFilterBox
              selectedMinDate={selectedMinDate}
              setSelectedMinDate={setSelectedMinDate}
              selectedMaxDate={selectedMaxDate}
              setSelectedMaxDate={setSelectedMaxDate}
              onClearDates={() => {
                setSelectedMinDate(null);
                setSelectedMaxDate(null);
              }}
            />
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Export buttons */}
          <button
            onClick={exportToExcel}
            title={t("transactionsTable.exportExcel")}
            className="
              group shrink-0
              h-10 px-3 sm:px-4 rounded-xl
              flex items-center justify-center gap-1.5
              text-xs font-bold
              text-emerald-700 dark:text-emerald-300
              bg-emerald-500/10 hover:bg-emerald-500/20
              border border-emerald-500/30 hover:border-emerald-400/60
              backdrop-blur-md
              transition-all duration-200
              hover:scale-105 active:scale-95
            "
          >
            <FileSpreadsheet size={14} />
            <span className="hidden sm:inline">{t("transactionsTable.exportExcelShort")}</span>
          </button>

          <button
            onClick={exportToPDF}
            title={t("transactionsTable.exportPdf")}
            className="
              group shrink-0
              h-10 px-3 sm:px-4 rounded-xl
              flex items-center justify-center gap-1.5
              text-xs font-bold
              text-rose-700 dark:text-rose-300
              bg-rose-500/10 hover:bg-rose-500/20
              border border-rose-500/30 hover:border-rose-400/60
              backdrop-blur-md
              transition-all duration-200
              hover:scale-105 active:scale-95
            "
          >
            <FileText size={14} />
            <span className="hidden sm:inline">{t("transactionsTable.exportPdfShort")}</span>
          </button>
        </div>
      </div>

      {/* ============================================================
       *  Table
       * ============================================================ */}
      <div className="overflow-x-auto rounded-xl border border-teal-500/15 dark:border-teal-400/15">
        <table
          {...getTableProps()}
          className="w-full min-w-[840px] text-sm"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
                className="bg-gradient-to-l from-teal-500/5 via-transparent to-amber-300/5 border-b border-teal-500/15 dark:border-teal-400/15"
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="
                      px-3 py-3 text-center text-[11px] font-bold
                      text-slate-500 dark:text-slate-400 tracking-wide
                      cursor-pointer whitespace-nowrap select-none
                      hover:text-amber-600 dark:hover:!text-amber-300
                      transition-colors duration-200
                    "
                  >
                    <span className="inline-flex items-center gap-1">
                      <span className="h-1 w-1 rounded-full bg-amber-400" />
                      {column.render("Header")}
                      {column.isSorted && (
                        <span className="text-[9px] text-teal-500">
                          {column.isSortedDesc ? "▼" : "▲"}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    key={row.id || row.original.id}
                    {...row.getRowProps()}
                    className="border-b border-teal-500/8 dark:border-teal-400/8 last:border-0 hover:bg-teal-500/5 dark:hover:!bg-teal-400/5 transition-colors duration-200"
                  >
                    {row.cells.map((cell) => (
                      <td
                        key={cell.column.id}
                        {...cell.getCellProps()}
                        className="px-3 py-3 text-center whitespace-nowrap"
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
                    <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                      <Layers size={22} className="animate-pulse text-teal-400/70" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                      {t("transactionsTable.emptyState.title")}
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                      {t("transactionsTable.emptyState.hint")}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ============================================================
       *  Pagination
       * ============================================================ */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-teal-500/10 dark:border-teal-400/10">

        {/* Rows per page */}
        <div className="flex items-center gap-2 text-[11px] sm:text-xs">
          <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {t("transactionsTable.pagination.rowsPerPage")}
          </span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="
              appearance-none px-2.5 py-1.5 rounded-lg
              text-xs font-bold tabular-nums
              bg-white/60 dark:bg-slate-800/60
              text-slate-700 dark:text-slate-200
              border border-teal-500/20 dark:border-teal-400/20
              focus:outline-none focus:ring-2 focus:ring-amber-300/60
              cursor-pointer
            "
          >
            {[5, 10, 20].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page controls */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="
              h-9 px-3 rounded-lg
              flex items-center gap-1
              text-xs font-bold
              text-teal-700 dark:text-amber-200
              bg-white/60 dark:bg-white/5
              border border-teal-500/20 dark:border-teal-400/20
              hover:border-amber-300/60 hover:bg-amber-300/10
              transition-all duration-200
              hover:scale-105 active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
            "
          >
            <ChevronRight size={14} />
            <span className="hidden sm:inline">{t("transactionsTable.pagination.prev")}</span>
          </button>

          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {t("transactionsTable.pagination.page")}
            </span>
            <span className="text-xs font-extrabold text-amber-600 dark:text-amber-300 tabular-nums">
              {formatNumber(pageIndex + 1)}
            </span>
          </div>

          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className="
              h-9 px-3 rounded-lg
              flex items-center gap-1
              text-xs font-bold
              text-teal-700 dark:text-amber-200
              bg-white/60 dark:bg-white/5
              border border-teal-500/20 dark:border-teal-400/20
              hover:border-amber-300/60 hover:bg-amber-300/10
              transition-all duration-200
              hover:scale-105 active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
            "
          >
            <span className="hidden sm:inline">{t("transactionsTable.pagination.next")}</span>
            <ChevronLeft size={14} />
          </button>
        </div>
      </div>

      {/* ---------- Modal ---------- */}
      <TransactionInfoModal
        isOpen={isTransactionModalOpen}
        closeModal={closeModal}
        modalData={transactionModalData}
      />
    </div>
  );
};

export default TransactionsTable;